import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, rp, DISCOUNT_REASONS, OVERRIDE_REASONS } from "@/lib/api";
import { MediaManager } from "@/admin/MediaManager";
import { sel, L, BookingTotals, BookingStatusBar, AssignUnitsCard, AssignDriverCard, DepositRefundCard, PaymentsCard } from "@/admin/booking/BookingCards";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function BookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [units, setUnits] = useState([]);
  const [pay, setPay] = useState({ amount: "", payment_method: "Bank Transfer", payment_type: "Rental", notes: "" });
  const [refund, setRefund] = useState({ deduction_amount: 0, refunded_amount: 0, method: "Bank Transfer", returned_date: new Date().toISOString().slice(0, 10), reason: "" });
  const [settings, setSettings] = useState(null);
  const [damage, setDamage] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get(`/bookings/${id}`).then((r) => {
    setB(r.data);
    api.get(`/damage-pack/${id}`, { params: { base: window.location.origin } })
      .then((d) => setDamage(d.data)).catch(() => setDamage(null));
  }), [id]);
  useEffect(() => { load(); api.get("/units").then((r) => setUnits(r.data)); api.get("/settings").then((r) => setSettings(r.data));
    api.get("/staff/drivers").then((r) => setDrivers(r.data)).catch(() => setDrivers([])); }, [load]);

  const submitRefund = async () => {
    try {
      await api.post(`/bookings/${id}/deposit-refund`, {
        deduction_amount: Number(refund.deduction_amount || 0),
        refunded_amount: Number(refund.refunded_amount || 0),
        method: refund.method, returned_date: refund.returned_date, reason: refund.reason,
      });
      await load(); toast.success("Deposit refund recorded — customer can see the receipt in Check Booking");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const voidRefund = async (receiptNo) => {
    if (!window.confirm(`Undo refund ${receiptNo}? The amount will be removed from the total and payments.`)) return;
    try { await api.delete(`/bookings/${id}/deposit-refund/${receiptNo}`); await load(); toast.success("Refund undone"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const refundMessage = () => {
    const r = b?.deposit_receipt || {};
    const tpl = settings?.deposit_refund_template || "";
    return tpl.replace("{name}", b?.customer_name || "").replace("{code}", b?.booking_code || "")
      .replace("{deposit}", rp(r.deposit_amount || 0)).replace("{deduction}", rp(r.deduction_amount || 0))
      .replace("{refunded}", rp(r.refunded_amount || 0)).replace("{method}", r.method || "")
      .replace("{date}", r.returned_date || "");
  };

  const save = async (patch) => {
    try { await api.put(`/bookings/${id}`, patch); await load(); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this booking permanently? Payments and inspections will be removed too.")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      nav("/admin/bookings");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const assign = async (uid) => {
    const cur = b.assigned_units || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    try { await api.post(`/bookings/${id}/assign-units`, { unit_ids: next }); await load(); toast.success("Units updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const assignDriver = async (driverId) => {
    try { await api.post(`/bookings/${id}/assign-driver`, { driver_id: driverId || null }); await load(); toast.success("Driver updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addPayment = async () => {    if (!pay.amount) return;    await api.post("/payments", { ...pay, booking_id: id, amount: Number(pay.amount) });
    setPay({ ...pay, amount: "" }); await load(); toast.success("Payment recorded");
  };
  const syncMidtrans = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/payments/gateway/sync-status/${id}`);
      await load();
      (r.data.changed ? toast.success : toast.info)(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal cek status Midtrans"); }
    finally { setSyncing(false); }
  };

  useEffect(() => {
    if (b && refund.refunded_amount === 0 && refund.deduction_amount === 0) {
      setRefund((p) => ({ ...p, deduction_amount: b.deposit_deduction || 0, refunded_amount: Math.max(0, (b.deposit_amount || 0) - (b.deposit_deduction || 0)) }));
    }
    // eslint-disable-next-line
  }, [b]);

  if (!b) return <div className="text-ink-soft">Loading…</div>;

  return (
    <div data-testid="admin-booking-detail" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/bookings" className="text-xs text-lm-red">← Bookings</Link>
          <h1 className="font-display text-3xl tracking-tight" data-testid="bd-code">{b.booking_code}</h1>
          <p className="text-sm text-ink-soft">{b.customer_name} · {b.customer_whatsapp} · {b.source}</p>
        </div>
        <Link to={`/admin/customers?c=${b.customer_id}`} className="text-sm border border-black/15 rounded-md px-4 py-2">View Customer</Link>
        <div className="flex gap-2">
          <button data-testid="bd-cancel" onClick={() => save({ booking_status: "Cancelled" })}
            className="text-sm border border-black/15 rounded-md px-4 py-2 hover:border-lm-red">Cancel Booking</button>
          <button data-testid="bd-delete" onClick={remove}
            className="text-sm border border-lm-red text-lm-red rounded-md px-4 py-2">Delete</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <section className="bg-white border border-black/10 rounded-lg p-5 space-y-3">
          <h2 className="text-base font-semibold">Schedule & Package</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <L label="Rental Date"><Input data-testid="bd-date" type="date" value={b.rental_date} onChange={(e) => setB({ ...b, rental_date: e.target.value })} /></L>
            <L label="Start Time"><Input data-testid="bd-time" value={b.start_time} onChange={(e) => setB({ ...b, start_time: e.target.value })} /></L>
            <L label="Quantity"><Input data-testid="bd-qty" type="number" min={1} value={b.quantity} onChange={(e) => setB({ ...b, quantity: Number(e.target.value) })} /></L>
            <L label="Package Price (per unit)"><Input data-testid="bd-price" type="number" value={b.package_price_snapshot} onChange={(e) => setB({ ...b, package_price_snapshot: Number(e.target.value) })} /></L>
            <L label="Override Reason"><select className={sel} value={b.override_reason || ""} onChange={(e) => setB({ ...b, override_reason: e.target.value })}>
              <option value="">—</option>{OVERRIDE_REASONS.map((r) => <option key={r}>{r}</option>)}</select></L>
            <L label="Delivery Fee"><Input data-testid="bd-delivery-fee" type="number" value={b.delivery_fee} onChange={(e) => setB({ ...b, delivery_fee: Number(e.target.value) })} /></L>
            <L label="Vehicle"><select className={sel} value={b.vehicle_type || "car"} onChange={(e) => setB({ ...b, vehicle_type: e.target.value })}>
              <option value="bike">Bike (Lalamove)</option><option value="car">Car / MPV (Lalamove)</option><option value="self_pickup">Self Pickup</option></select></L>
            <L label="Pickup Fee"><Input type="number" value={b.pickup_fee} onChange={(e) => setB({ ...b, pickup_fee: Number(e.target.value) })} /></L>
            <L label="Actual Delivery Cost (internal)"><Input data-testid="bd-actual-cost" type="number" value={b.actual_delivery_cost || 0} onChange={(e) => setB({ ...b, actual_delivery_cost: Number(e.target.value) })} /></L>
            <L label="Manual Discount"><Input data-testid="bd-manual-discount" type="number" value={b.manual_discount} onChange={(e) => setB({ ...b, manual_discount: Number(e.target.value) })} /></L>
            <L label="Discount Reason"><select className={sel} value={b.manual_discount_reason || ""} onChange={(e) => setB({ ...b, manual_discount_reason: e.target.value })}>
              <option value="">—</option>{(settings?.discount_reasons || DISCOUNT_REASONS).map((r) => <option key={r}>{r}</option>)}</select></L>
            <L label="Deposit Amount"><Input data-testid="bd-deposit" type="number" value={b.deposit_amount} onChange={(e) => setB({ ...b, deposit_amount: Number(e.target.value) })} /></L>
            <L label="Promo Discount"><Input type="number" value={b.promo_discount} onChange={(e) => setB({ ...b, promo_discount: Number(e.target.value) })} /></L>
          </div>
          <L label="Internal Notes"><Textarea rows={2} value={b.internal_notes || ""} onChange={(e) => setB({ ...b, internal_notes: e.target.value })} /></L>
          <button data-testid="bd-save" onClick={() => save({
            rental_date: b.rental_date, start_time: b.start_time, quantity: b.quantity,
            package_price_snapshot: b.package_price_snapshot, override_reason: b.override_reason,
            delivery_fee: b.delivery_fee, pickup_fee: b.pickup_fee, actual_delivery_cost: b.actual_delivery_cost,
            manual_discount: b.manual_discount, manual_discount_reason: b.manual_discount_reason,
            deposit_amount: b.deposit_amount, promo_discount: b.promo_discount, internal_notes: b.internal_notes,
          })} className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">Save Changes</button>
        </section>

        <div className="space-y-5">
          <BookingTotals b={b} />
          <BookingStatusBar b={b} save={save} />
          <AssignUnitsCard b={b} units={units} assign={assign} />
          <AssignDriverCard b={b} drivers={drivers} assignDriver={assignDriver} />
          <DepositRefundCard b={b} refund={refund} setRefund={setRefund} submitRefund={submitRefund} voidRefund={voidRefund}
            refundWaHref={`https://wa.me/${(b.customer_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent(refundMessage())}`}
            damage={damage} />
          <PaymentsCard b={b} pay={pay} setPay={setPay} addPayment={addPayment}
            syncMidtrans={syncMidtrans} syncing={syncing} />
        </div>
      </div>

      <MediaManager bookingId={id} />
    </div>
  );
}
